import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentDataSampleDTO, AgentDataSampleFormValues } from '../../types';
import { useAgentDataSampleAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const EMPTY_FORM: AgentDataSampleFormValues = { Name: '', Description: '', Content: '', IsActive: true };

/**
 * Restored Data Samples tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_AgentDataSamples) confirmed by reading AgentDataSample.cs directly:
 * Name/Description/Content (one free-form field)/IsActive — no input/output split, no
 * SampleType enum, exactly as briefed. See agentDataSampleAdminApiClient.ts for the confirmed
 * `POST update`/`POST delete` route-shape quirk (still the same generic UpdateWebRequest/
 * DeleteWebRequest DTOs, just a different HTTP verb/path than every sibling controller here).
 */
export function DataSamplesPanel({ agentId }: { agentId: number }) {
  const api = useAgentDataSampleAdminApiClient();
  const [samples, setSamples] = useState<AgentDataSampleDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentDataSampleDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentDataSampleFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentDataSampleDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getDataSamplesByAgent(agentId);
    setSamples(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (sample: AgentDataSampleDTO | 'new') => {
    setEditing(sample);
    setError(null);
    setForm(
      sample === 'new'
        ? EMPTY_FORM
        : { Name: sample.Name, Description: sample.Description ?? '', Content: sample.Content, IsActive: sample.IsActive }
    );
  };

  const handleSave = async () => {
    if (!form.Name.trim() || !form.Content.trim()) {
      setError('Name and Content are required.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = editing === 'new' ? await api.createDataSample(agentId, form) : await api.updateDataSample((editing as AgentDataSampleDTO).AgentDataSampleID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to save.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteDataSample(pendingDelete.AgentDataSampleID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setSamples((prev) => prev.filter((s) => s.AgentDataSampleID !== pendingDelete.AgentDataSampleID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Data Samples" />
        <div className="aaa-form-group aaa-form-group-full">
          <label>Name *</label>
          <input type="text" value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Description</label>
          <input type="text" value={form.Description ?? ''} onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Content *</label>
          <textarea rows={8} value={form.Content} onChange={(e) => setForm((f) => ({ ...f, Content: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label className="aaa-checkbox">
            <input type="checkbox" checked={form.IsActive} onChange={(e) => setForm((f) => ({ ...f, IsActive: e.target.checked }))} />
            Active
          </label>
        </div>
        {error && <div className="aaa-editor-error aaa-form-group-full">{error}</div>}
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Data Sample'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Data Samples" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Data Sample
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && samples.length === 0 && <div className="aaa-entity-dropdown-empty">No data samples yet.</div>}
      <div className="aaa-entity-grid">
        {samples.map((sample) => (
          <div key={sample.AgentDataSampleID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{sample.Name}</strong>
              <span className={`aaa-badge ${sample.IsActive ? 'aaa-badge-active' : 'aaa-badge-inactive'}`}>
                {sample.IsActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p>{sample.Content.length > 140 ? `${sample.Content.slice(0, 140)}…` : sample.Content}</p>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(sample)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(sample)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Data Sample"
        message={`Delete data sample "${pendingDelete?.Name}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
